import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createTicket, getTickets, updateTicket, getUploadUrl, uploadFileToS3 } from "../api";
import { formatDate, formatRelative, formatDateTime } from "../utils/dateFormat";
import SecureImage from "../components/SecureImage";

const ISSUE_TYPES = [
  { icon: "📅", label: "Reschedule", desc: "Need to change the date or time" },
  { icon: "❌", label: "Cancellation", desc: "Want to cancel this booking" },
  { icon: "🔧", label: "Work not completed", desc: "Job was not finished as agreed" },
  { icon: "😤", label: "Unprofessional behavior", desc: "Pro was rude or unprofessional" },
  { icon: "💰", label: "Billing issue", desc: "Charged incorrectly or overcharged" },
  { icon: "🚫", label: "No show", desc: "Pro did not show up" },
  { icon: "⚠️", label: "Safety concern", desc: "Safety issue during service" },
  { icon: "❓", label: "Other", desc: "Something else" },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  open:      { label: "Open",      color: "text-yellow-700", bg: "bg-yellow-50",  dot: "bg-yellow-400" },
  in_review: { label: "In Review", color: "text-mouna-blue", bg: "bg-blue-pale",  dot: "bg-mouna-blue" },
  escalated: { label: "Escalated", color: "text-red-600",    bg: "bg-red-50",     dot: "bg-red-500" },
  resolved:  { label: "Resolved",  color: "text-teal",       bg: "bg-teal-light", dot: "bg-teal" },
  closed:    { label: "Closed",    color: "text-gray-500",   bg: "bg-gray-100",   dot: "bg-gray-400" },
};

const TABS = [
  { key: "open", label: "Open" },
  { key: "in_review", label: "In Review" },
  { key: "resolved", label: "Resolved" },
];

export default function TicketPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId") || "";
  const proId = searchParams.get("proId") || "";

  const [view, setView] = useState<"list" | "new" | "detail">("list");
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [issueType, setIssueType] = useState("");
  const [description, setDescription] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);
  const [evidencePreviews, setEvidencePreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("open");

  const userRole = localStorage.getItem("mouna_user_role") || "client";
  const userId = (() => {
    const k = Object.keys(localStorage).find(key => key.includes("idToken"));
    if (!k) return "";
    const t = localStorage.getItem(k);
    if (!t) return "";
    try { return JSON.parse(atob(t.split(".")[1])).sub || ""; } catch { return ""; }
  })();

  useEffect(() => {
    loadTickets();
    if (bookingId) setView("new");
  }, []);

  async function loadTickets() {
    try { const data = await getTickets(); setTickets(data || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function handleSubmit() {
    if (!issueType || !description.trim()) { setError("Please select an issue type and describe the problem"); return; }
    setSubmitting(true); setError("");
    try {
      const urls: string[] = [];
      for (const file of evidenceFiles) {
        const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, "ticket-evidence");
        await uploadFileToS3(uploadUrl, file);
        urls.push(fileUrl);
      }
      await createTicket(bookingId, proId, issueType, description, urls);
      setSuccess(true);
      await loadTickets();
    } catch { setError("Failed to submit. Please try again."); }
    finally { setSubmitting(false); }
  }

  async function handleReply() {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      await updateTicket(selectedTicket.ticketId, { message: replyText, status: selectedTicket.status });
      setReplyText("");
      await loadTickets();
      const updated = tickets.find(t => t.ticketId === selectedTicket.ticketId);
      if (updated) setSelectedTicket(updated);
    } catch { console.error("Reply failed"); }
  }

  async function handleEscalate() {
    if (!selectedTicket) return;
    await updateTicket(selectedTicket.ticketId, { status: "escalated", message: "Client escalated this ticket for admin review." });
    await loadTickets();
    setSelectedTicket({ ...selectedTicket, status: "escalated" });
  }

  const filteredTickets = tickets.filter(t =>
    activeTab === "open" ? (t.status === "open" || t.status === "in_review") :
    activeTab === "in_review" ? t.status === "escalated" :
    t.status === "resolved" || t.status === "closed"
  );

  if (success) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
      <div className="text-center animate-slide-up">
        <div className="w-20 h-20 bg-teal-light rounded-full flex items-center justify-center text-4xl mx-auto mb-4">✅</div>
        <h1 className="font-display text-2xl font-bold text-navy mb-2">We're on it!</h1>
        <p className="text-sm text-gray-500 mb-1">Your ticket has been submitted.</p>
        <p className="text-xs text-gray-400 mb-6">The pro has 24 hours to respond. If they don't, we'll step in.</p>
        <div className="space-y-3">
          <button onClick={() => { setSuccess(false); setView("list"); }}
            className="btn btn-primary btn-full">Track My Ticket</button>
          <button onClick={() => navigate(-1)}
            className="btn btn-secondary btn-full">Back to Bookings</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-0 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => view === "list" ? navigate(-1) : setView("list")}
              className="text-gray-400 text-sm font-semibold mb-1 flex items-center gap-1">
              ← {view === "list" ? "Back" : "My Tickets"}
            </button>
            <h1 className="font-display text-2xl font-bold text-navy">
              {view === "list" ? "Support Tickets" : view === "new" ? "Get Help" : "Ticket Details"}
            </h1>
          </div>
          {view === "list" && userRole !== "pro" && (
            <button onClick={() => setView("new")}
              className="btn btn-primary btn-sm">+ New</button>
          )}
        </div>

        {view === "list" && (
          <div className="flex gap-1">
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={"flex-1 py-3 text-xs font-bold transition-all relative " +
                  (activeTab === tab.key ? "text-mouna-blue" : "text-gray-400")}>
                {tab.label}
                {activeTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-mouna-blue rounded-full"/>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-5 pt-4 pb-24">

        {/* List view */}
        {view === "list" && (
          <>
            {loading && (
              <div className="space-y-3">
                {[1,2].map(i => (
                  <div key={i} className="bg-white rounded-2xl p-4">
                    <div className="skeleton h-4 w-1/3 rounded mb-2"></div>
                    <div className="skeleton h-5 w-2/3 rounded mb-2"></div>
                    <div className="skeleton h-3 w-1/2 rounded"></div>
                  </div>
                ))}
              </div>
            )}

            {!loading && filteredTickets.length === 0 && (
              <div className="text-center py-16 animate-fade-in">
                <div className="w-16 h-16 bg-teal-light rounded-full flex items-center justify-center text-3xl mx-auto mb-4">🎉</div>
                <div className="font-bold text-navy mb-2">
                  {userRole === "pro" ? "No issues reported" : "No open tickets"}
                </div>
                <div className="text-sm text-gray-400">
                  {userRole === "pro" ? "Keep up the great work!" : "Everything looks good!"}
                </div>
              </div>
            )}

            {filteredTickets.map(ticket => {
              const status = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
              return (
                <div key={ticket.ticketId}
                  onClick={() => { setSelectedTicket(ticket); setView("detail"); }}
                  className="bg-white rounded-2xl shadow-card mb-3 overflow-hidden cursor-pointer hover:shadow-card-hover transition-shadow animate-fade-in">
                  <div className={`px-4 py-2.5 flex items-center justify-between ${status.bg}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${status.dot}`}></div>
                      <span className={`text-xs font-bold ${status.color}`}>{status.label}</span>
                    </div>
                    <span className="text-xs text-gray-400">{formatRelative(ticket.createdAt)}</span>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                        {ISSUE_TYPES.find(i => i.label === ticket.issueType)?.icon || "❓"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-navy text-sm">{ticket.issueType}</div>
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{ticket.description}</div>
                        <div className="text-xs text-gray-400 mt-1">{(ticket.messages || []).length} messages</div>
                      </div>
                      <span className="text-gray-300 text-lg flex-shrink-0">›</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* New ticket form */}
        {view === "new" && (
          <div className="animate-slide-up">
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-500 text-sm rounded-2xl p-3 mb-4">{error}</div>
            )}

            <div className="bg-blue-pale rounded-2xl p-4 mb-5">
              <div className="font-bold text-navy text-sm mb-1">💡 We'll help you fix this</div>
              <div className="text-xs text-gray-500">The pro will be notified and has 24 hours to respond.</div>
            </div>

            <div className="mb-5">
              <label className="label mb-3">What's the issue?</label>
              <div className="space-y-2">
                {ISSUE_TYPES.map(issue => (
                  <button key={issue.label} onClick={() => setIssueType(issue.label)}
                    className={"w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 transition-all text-left " +
                      (issueType === issue.label ? "bg-mouna-blue border-mouna-blue text-white" : "bg-white border-gray-200 hover:border-gray-300")}>
                    <span className="text-xl">{issue.icon}</span>
                    <div className="flex-1">
                      <div className="font-bold text-sm">{issue.label}</div>
                      <div className={`text-xs ${issueType === issue.label ? "text-blue-200" : "text-gray-400"}`}>{issue.desc}</div>
                    </div>
                    {issueType === issue.label && <span className="text-white text-lg">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="label">Describe the issue</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
                placeholder="Tell us what happened in detail..."
                className="input resize-none"/>
            </div>

            <div className="mb-6">
              <label className="label mb-2">Add Evidence (optional)</label>
              <div className="text-xs text-gray-400 mb-3">Photos and videos help resolve issues faster</div>
              {evidencePreviews.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {evidencePreviews.map((p, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                      <img src={p} alt="" className="w-full h-full object-cover"/>
                      <button onClick={() => {
                        setEvidenceFiles(prev => prev.filter((_, idx) => idx !== i));
                        setEvidencePreviews(prev => prev.filter((_, idx) => idx !== i));
                      }} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center">✕</button>
                    </div>
                  ))}
                </div>
              )}
              <label htmlFor="evidence-upload"
                className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-2xl py-5 cursor-pointer hover:border-mouna-blue transition-colors">
                <span className="text-2xl">📎</span>
                <span className="text-sm text-gray-500 font-semibold">Add Photos or Videos</span>
              </label>
              <input id="evidence-upload" type="file" accept="image/*,video/*" multiple className="hidden"
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  setEvidenceFiles(prev => [...prev, ...files]);
                  files.forEach(file => {
                    const reader = new FileReader();
                    reader.onload = ev => setEvidencePreviews(prev => [...prev, ev.target?.result as string]);
                    reader.readAsDataURL(file);
                  });
                }}/>
            </div>

            <button onClick={handleSubmit} disabled={submitting}
              className="btn btn-primary btn-full btn-lg">
              {submitting ? "Submitting..." : "Submit Ticket"}
            </button>
          </div>
        )}

        {/* Detail view */}
        {view === "detail" && selectedTicket && (() => {
          const status = STATUS_CONFIG[selectedTicket.status] || STATUS_CONFIG.open;
          const messages = selectedTicket.messages || [];
          return (
            <div className="animate-slide-up">
              <div className={`rounded-2xl p-4 mb-4 ${status.bg}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2.5 h-2.5 rounded-full ${status.dot}`}></div>
                  <span className={`font-bold text-sm ${status.color}`}>{selectedTicket.issueType}</span>
                </div>
                <div className={`text-xs ${status.color} opacity-70`}>Status: {status.label}</div>
                {selectedTicket.status === "open" && (
                  <div className="text-xs text-gray-500 mt-1">Pro has 24 hours to respond.</div>
                )}
                {selectedTicket.estimatedResolutionTime && (
                  <div className={`text-xs font-bold mt-1 ${status.color}`}>
                    ⏱ Expected: {selectedTicket.estimatedResolutionTime}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-4 shadow-card mb-4">
                <div className="label mb-2">Your Report</div>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.description}</p>
                {selectedTicket.evidenceUrls?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {selectedTicket.evidenceUrls.map((url: string, i: number) => (
                      <SecureImage key={i} src={url} alt="" className="w-full aspect-square object-cover rounded-xl"/>
                    ))}
                  </div>
                )}
              </div>

              {messages.length > 0 && (
                <div className="mb-4">
                  <div className="label mb-3">Conversation</div>
                  {messages.map((msg: any, i: number) => {
                    if (msg.isSystem) return (
                      <div key={i} className="flex justify-center mb-3">
                        <div className="bg-blue-pale text-mouna-blue text-xs rounded-2xl px-4 py-2 text-center max-w-xs">{msg.message}</div>
                      </div>
                    );
                    if (msg.requestType === "more_info") return (
                      <div key={i} className="flex justify-center mb-3">
                        <div className="bg-yellow-50 border border-yellow-100 text-yellow-700 text-xs rounded-2xl px-4 py-3 text-center max-w-xs">
                          <div className="font-bold mb-1">📋 Action Required</div>
                          {msg.message.replace("📋 Please provide more information: ", "")}
                        </div>
                      </div>
                    );
                    return (
                      <div key={i} className={"flex mb-3 " + (msg.senderId === userId ? "justify-end" : "justify-start")}>
                        {msg.senderId !== userId && (
                          <div className="w-8 h-8 rounded-full bg-mouna-blue flex items-center justify-center text-white text-xs font-bold mr-2 flex-shrink-0">
                            {msg.senderRole === "admin" ? "A" : "P"}
                          </div>
                        )}
                        <div className={"max-w-xs px-4 py-3 rounded-2xl " +
                          (msg.senderId === userId ? "bg-mouna-blue text-white rounded-tr-sm" : "bg-white text-navy rounded-tl-sm shadow-card")}>
                          <div className="text-xs font-bold mb-1 opacity-70">
                            {msg.senderRole === "admin" ? "Mouna Support" : msg.senderRole === "pro" ? "Pro" : "You"}
                          </div>
                          <div className="text-sm">{msg.message}</div>
                          <div className={`text-xs mt-1 ${msg.senderId === userId ? "text-blue-200" : "text-gray-400"}`}>
                            {formatDateTime(msg.sentAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedTicket.resolution && (
                <div className="bg-teal-light rounded-2xl p-4 mb-4">
                  <div className="label text-teal mb-1">Resolution</div>
                  <p className="text-sm text-gray-700">{selectedTicket.resolution}</p>
                </div>
              )}

              {!["resolved", "closed"].includes(selectedTicket.status) && (
                <div className="bg-white rounded-2xl p-4 shadow-card mb-4">
                  <label className="label mb-2">Add a message</label>
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={3}
                    placeholder="Provide more details..."
                    className="input resize-none mb-3"/>
                  <button onClick={handleReply} disabled={!replyText.trim()}
                    className="btn btn-primary btn-full">Send Message</button>
                </div>
              )}

              {selectedTicket.status === "open" && messages.length >= 1 && (
                <div className="bg-red-50 rounded-2xl p-4">
                  <div className="font-bold text-red-600 text-sm mb-1">Still not resolved?</div>
                  <div className="text-xs text-gray-500 mb-3">Escalate to our team for immediate review.</div>
                  <button onClick={handleEscalate}
                    className="w-full bg-red-500 text-white rounded-full py-3 font-bold text-sm">
                    Escalate to Mouna Support
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
